/*
 *   File : detail.js
 *   Author : https://evoqins.com
 *   Description : Invoice detail page
 *   Integrations :
 *   Version : 1.0.0
*/

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal } from 'bootstrap';

//custom component
import { DataTableComponent } from '../../../Component/DataTable';
import { DownloadButton, PrimaryButton, SecondaryButton, TextButton } from '../../../Component/Buttons';
import { APIService } from '../../../Service';
import { Loader } from '../../../Component/Others';
import { EmptyScreen } from '../../../Component/EmptyComponent';
import { ApproveUploadModal, PreviewSalesBill, RejectModal, SalesHistoryModal, SuccessModal } from '../../../Component/Modal';
import { Icon } from '../../../Component/Icon';
import { toast } from 'react-toastify';

import { ReactComponent as ArrowDown } from "../../../Assets/Image/table-arrow-down.svg";
import { ReactComponent as ArrowUp } from "../../../Assets/Image/table-arrow-up.svg";
import { SummaryCard } from '../../../Component/Cards';

const ROWS_PER_PAGE = 20;

const OCR_DETAIL_SORT_FIELDS = [
    'product_name',
    'code',
    'points',
    'quantity',
    'status',
];

const InvoiceDetail = () => {

    const modalContent = useRef({})
    const scrollRef = useRef(null);
    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    const navigate = useNavigate();
    const { state } = useLocation();

    const [approveId, setApproveId] = useState(null);
    const [approveData, setApproveData] = useState(null);
    const [productCode, setProductCode] = useState(null);
    const [approveSuccess, setApproveSuccess] = useState(false);
    const [rejectId, setRejectId] = useState(null);

    const [dataLoading, setDataLoading] = useState(true);
    const [ocrList, setOcrList] = useState({});
    const [modalBtnLoader, setModalBtnLoader] = useState(false);

    const [editID, setEditID] = useState(null);
    const [revertSuccess, setRevertSuccess] = useState(false);

    const [showSalesBillPreview, setShowSalesBillPreview] = useState(false);
    const [selectedSalesHistoryData, setSelectedSalesHistoryData] = useState(null);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });
    }, []);

    useEffect(() => {
        if (state !== null) {
            if (skipListFetchRef.current) {
                skipListFetchRef.current = false;
                return;
            }
            _getInvoiceDetail();
        } else {
            navigate("/invoices");
        }
    }, [state]);

    useEffect(() => {
        if (approveId !== null || editID !== null) {
            const approve_upload = new Modal(document.getElementById('approve-upload-modal'));
            approve_upload.show();
        }
    }, [approveId, editID]);

    useEffect(() => {
        if (approveSuccess === true) {
            const success_modal = new Modal(document.getElementById('success-modal'));
            success_modal.show();
        }
    }, [approveSuccess]);

    useEffect(() => {
        if (rejectId !== null) {
            const reject_modal = new Modal(document.getElementById('reject-modal'));
            reject_modal.show();
        }
    }, [rejectId]);

    useEffect(() => {
        if (showSalesBillPreview) {
            const sales_preview_modal = new Modal(document.getElementById('preview-sale-history-modal'));
            sales_preview_modal.show();
        }
    }, [showSalesBillPreview]);

    // API - Reject product
    const _rejectProduct = (request) => {
        setModalBtnLoader(true);

        let is_post = true;
        let url = 'CA/ocr/reject-product';
        let data = {
            "product_id": request.id,
            "rejection_reason": request.message ? request.message : ""
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                modalContent.current = {
                    type: "reject",
                    title: "Rejected successfully",
                    description: "Successfully rejected the application."
                }
                setApproveSuccess(true);
            } else {
                setApproveSuccess(false);
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setModalBtnLoader(false);
        });
    };

    // API - Approve product
    const _approveProduct = (request) => {
        setModalBtnLoader(true);

        let is_post = true;
        let url = 'CA/ocr/approve-product';
        let data = {
            "id": request.id,
            "product_id": request.product_id,
            "quantity": request.quantity,
            "line_amount": request.line_amount,
            "product_description": request.product_description,
            "receipt_number": request.receipt_number,
            "receipt_date": request.receipt_date,
            "total_amount": request.total_amount,
            "corrected_comment": request.corrected_comment
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                modalContent.current = {
                    type: "success",
                    title: "Approved successfully",
                    description: "Successfully approved the application."
                }
                setApproveSuccess(true);
            } else {
                setApproveSuccess(false);
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setModalBtnLoader(false);
        });
    };

    // API - Revert product
    const _revertProduct = (request) => {
        setModalBtnLoader(true);

        let is_post = true;
        let url = 'CA/ocr/revert-product';
        let data = {
            "id": request.id,
            "product_id": request.product_id
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                navigate(window.location.pathname, {
                    state: {
                        tab_index: 1,
                        current_page: 1
                    }
                });
                setRevertSuccess(true);
                toast.dismiss();
                toast.success(response.message, {
                    type: "success",
                    className: "e-toast",
                });
            } else {
                setRevertSuccess(false)
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setModalBtnLoader(false);
        });
    };

    const [detailData, setDetailData] = useState({});
    const [loader, setLoader] = useState(true);

    // API - Get Invoice Detail
    const _getInvoiceDetail = (sort_by = sortField, sort_order = sortDirection) => {
        setLoader(true);
        let is_post = true;
        let url = 'CA/ocr/detail';
        let data = {
            "reciept_id": state?.id,
            "sort_by": sort_by ? [sort_by] : null,
            "order": sort_order ? [sort_order] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setDetailData(response.data);
            } else {
                setDetailData({});
            }
            setLoader(false);
        });
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !OCR_DETAIL_SORT_FIELDS.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setLoader(true);
        _getInvoiceDetail(sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    const COL_WIDTHS = {
        sales_specialist: 180,
        store: 220,
        date: 150,
        customer_type: 150,
        total_points: 120,
        product_name: 280,
        product_code: 140,
        quantity: 100,
        status: 160,
        rejection_reason: 230,
        action: 200,
    };

    const COL_GRID_TEMPLATE = Object.values(COL_WIDTHS)
        .map((width) => `${width}px`)
        .join(' ');

    const INVOICE_DETAIL_TABLE_WIDTH = Object.values(COL_WIDTHS)
        .reduce((sum, width) => sum + width, 0);

    const _cellText = (value, inGrid = false) => (
        <p
            className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px text-truncate pe-1'
            style={inGrid ? { maxWidth: '100%' } : undefined}
            title={value}
        >
            {value || "-"}
        </p>
    );

    const _getTableRow = () => {
        if (!detailData || Object.keys(detailData).length === 0) {
            return null;
        }

        const parent = detailData.data || detailData;
        const rawProducts = parent.product_info || parent.data || parent.data?.data || [];
        const products = Array.isArray(rawProducts)
            ? rawProducts
            : (Array.isArray(rawProducts?.data) ? rawProducts.data : []);

        return {
            sales_specialist: parent.sales_specialist || parent.customer_name,
            store_name: parent.store_name,
            date_of_upload: parent.date_of_upload,
            customer_type: parent.customer_type || "-",
            total_quantity: parent.total_quantity || parent.quantity || "-",
            total_points: parent.total_points ?? parent.points ?? "-",
            product_info: products.map((item) => ({
                ...item,
                name: item.name || item.product_name || "-",
                code: item.code || item.product_code || "-",
                customer_type: item.customer_type || parent.customer_type || "-",
            })),
        };
    };

    const INVOICE_DETAIL_COLUMN = [
        {
            name: 'Sales specialist',
            selector: row => row.sales_specialist,
            cell: row => _cellText(row.sales_specialist),
            width: `${COL_WIDTHS.sales_specialist}px`,
            minWidth: `${COL_WIDTHS.sales_specialist}px`,
            sortable: false
        },
        {
            name: 'Store',
            selector: row => row.store_name,
            cell: row => _cellText(row.store_name),
            width: `${COL_WIDTHS.store}px`,
            minWidth: `${COL_WIDTHS.store}px`,
            sortable: false
        },
        {
            name: 'Date of upload',
            selector: row => row.date_of_upload || "-",
            cell: row => _cellText(row.date_of_upload),
            width: `${COL_WIDTHS.date}px`,
            minWidth: `${COL_WIDTHS.date}px`,
            sortable: false,
        },
        {
            name: 'Customer type',
            selector: row => row.customer_type || "-",
            cell: row => _cellText(row.customer_type),
            width: `${COL_WIDTHS.customer_type}px`,
            minWidth: `${COL_WIDTHS.customer_type}px`,
            sortable: false
        },
        {
            name: 'Total Points',
            id: 'points',
            sortField: 'points',
            selector: row => row.total_points || "-",
            cell: row => _cellText(row.total_points),
            width: `${COL_WIDTHS.total_points}px`,
            minWidth: `${COL_WIDTHS.total_points}px`,
            sortable: true
        },
        {
            name: 'Product name',
            id: 'product_name',
            sortField: 'product_name',
            selector: row => "-",
            cell: () => _cellText("-"),
            width: `${COL_WIDTHS.product_name}px`,
            minWidth: `${COL_WIDTHS.product_name}px`,
            sortable: true
        },
        {
            name: 'Product code',
            id: 'code',
            sortField: 'code',
            selector: row => "-",
            cell: () => _cellText("-"),
            width: `${COL_WIDTHS.product_code}px`,
            minWidth: `${COL_WIDTHS.product_code}px`,
            sortable: true
        },
        {
            name: 'Quantity',
            id: 'quantity',
            sortField: 'quantity',
            selector: row => row.total_quantity || "-",
            cell: row => _cellText(row.total_quantity),
            width: `${COL_WIDTHS.quantity}px`,
            minWidth: `${COL_WIDTHS.quantity}px`,
            sortable: true
        },
        {
            name: 'Status',
            id: 'status',
            sortField: 'status',
            selector: row => row.status,
            cell: () => <>-</>,
            width: `${COL_WIDTHS.status}px`,
            minWidth: `${COL_WIDTHS.status}px`,
            sortable: true
        },
        {
            name: 'Rejection reason',
            selector: () => "-",
            cell: () => <>-</>,
            width: `${COL_WIDTHS.rejection_reason}px`,
            minWidth: `${COL_WIDTHS.rejection_reason}px`,
            sortable: false
        },
        {
            name: 'Action',
            cell: () => <>-</>,
            width: `${COL_WIDTHS.action}px`,
            minWidth: `${COL_WIDTHS.action}px`,
            sortable: false
        }
    ];

    const ExpandableRows = ({ data }) => {
        if (!data.product_info || data.product_info.length === 0) {
            return <div className="p-3 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-16px e-bg-shocking-white">No data available.</div>;
        }

        return (
            <div className={`py-2 padding-32px-right d-flex flex-column gap-3 e-bg-shocking-white e-invoice-detail-expandable`}>
                {data.product_info.map((child_data, index) => (
                    <div
                        key={index}
                        className='e-invoice-detail-expandable-row align-items-center'
                        style={{ gridTemplateColumns: COL_GRID_TEMPLATE }}
                    >
                        {_cellText('"', true)}
                        {_cellText('"', true)}
                        {_cellText('"', true)}
                        {_cellText(child_data.customer_type, true)}
                        {_cellText(child_data.points ?? "-", true)}
                        {_cellText(child_data.name, true)}
                        {_cellText(child_data.code, true)}
                        {_cellText(child_data.quantity, true)}
                        <div className='e-invoice-detail-expandable-cell'>
                            {child_data.status && child_data.status !== "-" ?
                                <span className={`${child_data.status === "Success" ? "e-text-philippine-green" : child_data.status === "Approval Pending" ? "e-text-bright-orange" : "e-text-boston-university-red"} e-alt-font-noto-sans e-font-14 e-font-weight-500`}>
                                    {child_data.status}
                                </span>
                                :
                                <span className='e-alt-font-noto-sans e-font-14'>-</span>
                            }
                        </div>
                        {_cellText(
                            (child_data.status === "Rejected" || child_data.approval_status === "REJECTED")
                                ? (child_data.rejection_reason || "-")
                                : "-",
                            true
                        )}
                        <div className="d-flex gap-2 flex-shrink-0 e-invoice-detail-expandable-cell">

                            <PrimaryButton name="Review"
                                className={`padding-6px-tb padding-14px-lr e-accept`}
                                onPress={() => {
                                    setApproveId(child_data.id);
                                    setApproveData(child_data);
                                    setProductCode(child_data.name);
                                    setRejectId(null);
                                }}
                            />
                            <PrimaryButton name="Reject"
                                className="padding-6px-tb padding-14px-lr e-reject"
                                onPress={() => {
                                    setRejectId(child_data.id);
                                    setApproveId(null);
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const tableCustomStyles = {
        table: {
            style: {
                width: `${INVOICE_DETAIL_TABLE_WIDTH}px`,
                minWidth: `${INVOICE_DETAIL_TABLE_WIDTH}px`,
            },
        },
        headRow: {
            style: {
                width: `${INVOICE_DETAIL_TABLE_WIDTH}px`,
                minWidth: `${INVOICE_DETAIL_TABLE_WIDTH}px`,
            },
        },
        rows: {
            style: {
                width: `${INVOICE_DETAIL_TABLE_WIDTH}px`,
                minWidth: `${INVOICE_DETAIL_TABLE_WIDTH}px`,
            },
        },
        headCells: {
            style: {
                flexGrow: 0,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                overflow: 'visible',
                textOverflow: 'clip',
            },
        },
        cells: {
            style: {
                flexGrow: 0,
                flexShrink: 0,
                overflow: 'hidden',
            },
        },
        expanderRow: {
            style: {
                paddingLeft: '1.5rem',
            },
        },
    };

    const tableRow = _getTableRow();


    return (
        <section className='pb-5 '>
            <div className='e-bg-lavender-blush-light padding-12px-top padding-98px-bottom padding-52px-lr'>
                <div className='e-display-flex cursor-pointer'
                    onClick={() => navigate(-1)}>
                    <img src={require("../../../Assets/Image/CountryAdmin/arrow-left.svg").default}
                        draggable={false}
                        className='cursor-pointer'
                        alt='arrow'
                    />
                    <h1 className='ps-1 mb-0 e-primary-font-playfair e-font-24 line-height-32px e-text-rich-black e-font-weight-600'>
                        Manage Sales Invoices
                    </h1>
                </div>
            </div>
            <div className='margin-neg-66px-top padding-52px-lr'>
                {loader ?
                    <Loader />
                    :
                    <div ref={scrollRef} className='e-bg-white e-border-1px-platinum-dark e-admin-box-shadow py-4 e-border-radius-16'>

                        <div className='px-4'>
                            <h4 className='mb-3 e-alt-font-noto-sans e-font-20 line-height-28px e-text-rich-black e-font-weight-500'>
                                Scanned bill
                            </h4>

                            <div className='e-bg-beautiful-white e-border-radius-16 p-4'>
                                {(() => {
                                    const url = detailData.file_url?.split('?')[0];
                                    const fileExtension = url?.split('.').pop()?.toLowerCase();
                                    const isImage = ['jpg', 'jpeg', 'png', 'webp'].includes(fileExtension);
                                    const isPDF = fileExtension === 'pdf';

                                    if (!detailData.file_url) {
                                        return <span className='e-text-raisin-black e-alt-font-noto-sans e-font-14'>No bill available</span>;
                                    }
                                    if (isPDF) {
                                        return (
                                            <div className='d-flex flex-column gap-3 '>
                                                {/* <iframe
                                                    src={detailData.file_url}
                                                    title="Bill PDF"
                                                    className='e-border-1px-chinese-silver e-border-radius-16 w-100'
                                                    style={{ height: 366 }}
                                                /> */}
                                                <p className='mb-0 e-alt-font-noto-sans e-font-14 line-height-20px e-text-rich-black e-font-weight-500'>
                                                    {detailData.file_url?.split('/').pop() || detailData.file_url}
                                                </p>
                                                <div className='d-flex align-items-center gap-3 flex-wrap'>
                                                    <SecondaryButton
                                                        name={<><Icon icon="download-icon" className="me-1" size={20} /> Download</>}
                                                        className="e-text-indigo e-border-1px-indigo align-self-start"
                                                        padding="py-2 px-3"
                                                        onPress={() => {
                                                            const link = document.createElement('a');
                                                            link.href = detailData.file_url;
                                                            link.download = "invoice";
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            document.body.removeChild(link);
                                                        }}
                                                    />
                                                    <TextButton
                                                        name="Preview"
                                                        onPress={() => {
                                                            setSelectedSalesHistoryData(detailData.file_url);
                                                            setShowSalesBillPreview(true);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    }
                                    if (isImage) {
                                        return (
                                            <div className='d-flex align-items-end gap-3 flex-wrap'>
                                                <img
                                                    src={detailData.file_url}
                                                    height={366}
                                                    className='e-border-1px-chinese-silver cursor-pointer'
                                                    alt='invoice'
                                                    onClick={() => {
                                                        setSelectedSalesHistoryData(detailData.file_url);
                                                        setShowSalesBillPreview(true);
                                                    }}
                                                />
                                                <TextButton
                                                    name="Preview"
                                                    onPress={() => {
                                                        setSelectedSalesHistoryData(detailData.file_url);
                                                        setShowSalesBillPreview(true);
                                                    }}
                                                />
                                            </div>
                                        );
                                    }
                                    return (
                                        <img src={detailData.file_url}
                                            height={366}
                                            className='e-border-1px-chinese-silver'
                                            alt='invoice' />
                                    );
                                })()}
                            </div>
                        </div>

                        <div className='p-4'>
                            <div className='d-flex align-items-center justify-content-between pb-3'>
                                <h4 className='mb-0 e-alt-font-noto-sans e-font-20 line-height-28px e-text-rich-black e-font-weight-500'>
                                    Invoice details
                                </h4>

                                <SecondaryButton name={<div className='d-flex gap-1 justify-content-center align-items-center'>Download<Icon icon="download-icon" className="d-flex" size={24} /></div>}
                                    padding="padding-10px-all"
                                    className="w-210px e-border-1px-indigo e-text-indigo w-84px e-border-radius-16"
                                    onPress={() => {
                                        const link = document.createElement('a');
                                        link.href = detailData.file_url;
                                        link.download = "invoice";
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                    }} />
                            </div>

                            <div className='row row-cols-3 row-gap-3 padding-10px-top'>
                                <div className='col m-0'>
                                    <SummaryCard title="Invoice ID"
                                        loader={false}
                                        quantity={detailData.invoice_id || "-"} />
                                </div>
                            </div>
                        </div>



                        {tableRow ?
                            <div className='e-data-table-wrapper-striped e-table e-invoice-detail-table'>
                                <DataTableComponent
                                    key={`ocr-detail-table-${sortField || 'none'}-${sortDirection || 'none'}`}
                                    columns={INVOICE_DETAIL_COLUMN}
                                    data={[tableRow]}
                                    customStyles={tableCustomStyles}
                                    pagination={false}
                                    paginationServer={false}
                                    paginationPerPage={ROWS_PER_PAGE}
                                    paginationTotalRows={1}
                                    expandableRowsComponent={ExpandableRows}
                                    striped
                                    paginationDefaultPage={1}
                                    defaultExpandedRows={() => true}
                                    expandableRowsHideExpander
                                    expandableIcon={{ collapsed: false, expanded: false }}
                                    sortServer={true}
                                    defaultSortFieldId={sortField}
                                    defaultSortAsc={sortDirection !== 'desc'}
                                    onSort={_handleSort}
                                    onChangePage={() => { }}
                                    rowClick={(row) => { }}
                                />
                            </div>
                            :
                            // empty screen
                            <div className='py-5 my-5'>
                                <EmptyScreen message="No request found" />
                            </div>
                        }
                    </div>
                }
            </div>


            {approveId !== null &&
                <ApproveUploadModal title="Approve Upload"
                    primaryButtonLabel="Approve"
                    fileUrl={detailData.file_url}
                    currencyCode={detailData.currency_code}
                    id={approveId}
                    data={approveData}
                    productCode={productCode}
                    loader={modalBtnLoader}
                    status={approveSuccess}
                    handleClose={() => {
                        setTimeout(() => {
                            setProductCode(null);
                            setApproveId(null);
                            setApproveData(null);
                        }, 200);
                    }}
                    handleSuccess={_approveProduct}
                />
            }


            {editID !== null &&
                <ApproveUploadModal title="Edit invoice items"
                    primaryButtonLabel="Update"
                    id={editID}
                    fileUrl={detailData.file_url}
                    currencyCode={detailData.currency_code}
                    productCode={productCode}
                    loader={modalBtnLoader}
                    status={revertSuccess}
                    handleClose={() => {
                        setTimeout(() => {
                            setProductCode(null);
                            setEditID(null);
                            setRevertSuccess(false);
                        }, 200);
                    }}
                    handleSuccess={_revertProduct}
                />
            }


            {rejectId !== null &&
                <RejectModal title="Reject Upload"
                    id={rejectId}
                    loader={modalBtnLoader}
                    status={approveSuccess}
                    fileUrl={detailData.file_url}
                    handleClose={() => {
                        setTimeout(() => {
                            setRejectId(null);
                        }, 200);
                    }}
                    handleReject={_rejectProduct}
                />
            }

            {approveSuccess === true &&
                <SuccessModal title={modalContent?.current?.title}
                    description={modalContent?.current?.description}
                    handleClose={() => {
                        setTimeout(() => {
                            setApproveSuccess(false);
                            _getInvoiceDetail();

                            // if (modalContent.current.type === "reject") {
                            //     navigate(window.location.pathname, {
                            //         state: {
                            //             tab_index: 3,
                            //             current_page: 1
                            //         }
                            //     });
                            // } else {
                            //     navigate(window.location.pathname, {
                            //         state: {
                            //             tab_index: 2,
                            //             current_page: 1
                            //         }
                            //     });
                            // }
                            // _getOCRList();
                        }, 200);
                    }} />
            }

            {showSalesBillPreview &&
                <PreviewSalesBill file={selectedSalesHistoryData}
                    width="900px"
                    handleClose={() => {
                        setShowSalesBillPreview(false);
                        setSelectedSalesHistoryData(null);
                    }} />
            }
        </section>
    )
}

export default InvoiceDetail;